import React, { useState, useEffect } from 'react';

const UserManagement = ({ isOpen, onClose }) => {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    roles: 'admin'
  });
  const [addUserMessage, setAddUserMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      (async () => {
        await fetchUsers();
        await fetchProjects();
      })();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users');
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError('Failed to fetch users');
    }
  };

  const fetchProjects = async () => {
    try {
      // 获取当前登录用户的ID
      const currentUser = localStorage.getItem('userId') || '';
      // 添加user_id参数到请求URL
      const response = await fetch(`/api/projects?user_id=${currentUser}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch projects');
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setProjects(data);
      } else {
        throw new Error('Projects data is not an array');
      }
    } catch (err) {
      console.error('Project fetch error:', err);
      setError('Failed to fetch projects: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async (userId, updatedData) => {
    try {
      // 创建要提交的数据对象
      const dataToSubmit = {
        ...updatedData,
        allowedProjects: Array.isArray(updatedData.allowedProjects) 
          ? updatedData.allowedProjects 
          : []
      };
      
      // 如果密码字段为空，则从提交数据中移除
      if (dataToSubmit.password === "") {
        delete dataToSubmit.password;
      }
      
      // 移除_id字段，防止修改MongoDB不可变字段
      if (dataToSubmit._id) {
        delete dataToSubmit._id;
      }
      
      console.log("Submitting user data:", dataToSubmit);
      
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSubmit),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Server error:", errorData);
        throw new Error(errorData.error || 'Failed to update user');
      }
      
      fetchUsers(); // Refresh user list
    } catch (err) {
      console.error("Update user error:", err);
      setError(`Failed to update user: ${err.message}`);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserMessage('');
    
    try {
      if (!newUser.name || !newUser.email || !newUser.roles) {
        setAddUserMessage('Please fill in all required fields');
        return;
      }
      
      // 使用默认密码
      const userData = {
        ...newUser,
        password: 'Gemterra.com' // 默认密码
      };
      
      const response = await fetch('/api/users/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create user');
      }
      
      // 重置表单
      setNewUser({
        name: '',
        email: '',
        roles: 'admin'
      });
      
      setAddUserMessage('User created successfully');
      fetchUsers(); // 刷新用户列表
      setShowAddUserForm(false); // 关闭添加用户表单
    } catch (err) {
      console.error("Create user error:", err);
      setAddUserMessage(`Failed to create user: ${err.message}`);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[800px] max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">User Management</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="mb-4">
          <button 
            onClick={() => setShowAddUserForm(!showAddUserForm)}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            {showAddUserForm ? 'Cancel' : 'Add New User'}
          </button>
        </div>

        {showAddUserForm && (
          <div className="border rounded p-4 mb-6">
            <h3 className="font-medium mb-3">Create New User</h3>
            {addUserMessage && (
              <div className={`p-2 mb-3 rounded ${addUserMessage.includes('success') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {addUserMessage}
              </div>
            )}
            <form onSubmit={handleAddUser}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input 
                    type="text" 
                    name="name" 
                    value={newUser.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={newUser.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select 
                    name="roles" 
                    value={newUser.roles}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="admin">Admin</option>
                    <option value="office">Office</option>
                    <option value="pm">Project Manager</option>
                    <option value="consultant">Consultant</option>
                    <option value="marketing">Marketing</option>
                    <option value="site">Site</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button 
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Create User
                  </button>
                </div>
              </div>
              <div className="mt-2 text-sm text-gray-500">
                Default password will be set to "Gemterra.com"
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="text-center py-4">Loading...</div>
        ) : (
          <div className="space-y-4">
            {users.map(user => (
              <UserRow 
                key={user._id}
                user={user}
                projects={projects}
                onSave={handleSaveUser}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const UserRow = ({ user, projects, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [userData, setUserData] = useState({
    ...user,
    allowedProjects: Array.isArray(user.allowedProjects) ? [...user.allowedProjects] : []
  });

  const handleChange = (field, value) => {
    setUserData(prev => ({ ...prev, [field]: value }));
  };

  const handleProjectToggle = (projectId) => {
    // 创建一个新的allowedProjects数组副本
    let newProjects = [...userData.allowedProjects];
    
    // 判断项目是否已经在允许列表中
    const projectIndex = newProjects.indexOf(projectId);
    
    // 更新项目列表
    if (projectIndex >= 0) {
      // 如果已存在，则移除
      newProjects.splice(projectIndex, 1);
    } else {
      // 如果不存在，则添加
      newProjects.push(projectId);
    }
    
    console.log("Updated allowedProjects:", newProjects);
    setUserData(prev => ({ ...prev, allowedProjects: newProjects }));
  };

  // 检查项目是否在用户的允许列表中
  const isProjectAllowed = (projectId) => {
    return Array.isArray(userData.allowedProjects) && 
           userData.allowedProjects.includes(projectId);
  };

  const handleSave = () => {
    onSave(user._id, userData);
    setEditing(false);
  };

  // 增加防御性检查
  if (!Array.isArray(projects) || projects.length === 0) {
    console.error("projects is not a valid array");
    return (
      <div className="border rounded p-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-medium">{user.name}</h3>
            <p className="text-sm text-gray-500">{user.email}</p>
            <p className="text-sm text-gray-500">Role: {user.roles}</p>
          </div>
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            Edit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded p-4">
      {editing ? (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Username</label>
            <input
              type="text"
              value={userData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="mt-1 block w-full rounded border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Role</label>
            <select
              value={userData.roles}
              onChange={(e) => handleChange('roles', e.target.value)}
              className="mt-1 block w-full rounded border-gray-300 shadow-sm"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="pm">Project Manager</option>
              <option value="office">Office</option>
              <option value="site">Site</option>
              <option value="marketing">Marketing</option>
              <option value="consultant">Consultant</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">New Password</label>
            <input
              type="password"
              onChange={(e) => handleChange('password', e.target.value)}
              className="mt-1 block w-full rounded border-gray-300 shadow-sm"
              placeholder="Leave blank to keep current password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Allowed Projects</label>
            <div className="mt-2 space-y-2">
              {projects.map(project => (
                <label key={project._id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isProjectAllowed(project._id)}
                    onChange={() => handleProjectToggle(project._id)}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <span className="ml-2">{project.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-medium">{user.name}</h3>
            <p className="text-sm text-gray-500">{user.email}</p>
            <p className="text-sm text-gray-500">Role: {user.roles}</p>
          </div>
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            Edit
          </button>
        </div>
      )}
    </div>
  );
};

export default UserManagement;